import React, { Component, ErrorInfo, ReactNode } from 'react';
import { analyticsService } from '../../lib/analytics/analyticsService';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  sectionId?: string;
  sectionName?: string;
  componentName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Track the error in analytics
    analyticsService.trackEvent({
      event_type: 'error',
      event_name: 'app_error',
      event_data: {
        error_message: error.message,
        error_type: error.name,
        component: this.props.componentName || 'Unknown',
        section_id: this.props.sectionId,
        section_name: this.props.sectionName,
        stack: error.stack,
        component_stack: errorInfo.componentStack,
        url: window.location.href,
        timestamp: new Date().toISOString()
      }
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // Render fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="p-4 bg-red-50 rounded-lg border border-red-200 text-red-700">
          <h2 className="text-lg font-semibold mb-2">Une erreur est survenue</h2>
          <p className="mb-2">Nous sommes désolés, une erreur inattendue s'est produite.</p>
          <p className="text-sm text-red-600">{this.state.error?.message}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Recharger la page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
